import axios from "axios";

import { getPACaseCreateStatusAPI } from "@/api/get";

interface PACaseCreateResponseError {
  message?: string;
  error_code?: string;
  details?: Record<string, string>;
}

export interface PACaseCreateError {
  message: string;
  errorCode?: string;
  details?: Record<string, string>;
  analyticsEvent?: string;
}

type PACaseCreateErrorResolution =
  | {
    caseId: number;
    error: null;
  }
  | {
    caseId: null;
    error: PACaseCreateError;
  };

const ERROR_CODE_TO_ANALYTICS_EVENT: Record<string, string> = {
  MEMBER_NOT_FOUND: "caseCreateMemberNotFound",
  INVALID_NDC: "caseCreateInvalidNdc",
  INVALID_PROVIDER: "caseCreateInvalidProvider",
  DRUG_NOT_COVERED: "caseCreateDrugNotCovered",
  INVALID_QUESTIONNAIRE: "caseCreateInvalidQuestionnaire",
  DUPLICATE_CASE: "caseCreateDuplicateCase",
  PA_TOOL_NOT_MIGRATED: "caseCreatePaToolNotMigrated",
  EPA_CONFIG_MISSING: "caseCreateEpaConfigMissing",
  INVALID_ICD10: "caseCreateInvalidIcd10",
  RENEWAL_CODE_NOT_FOUND: "caseCreateRenewalCodeNotFound",
  RENEWAL_CODE_ALREADY_USED: "caseCreateRenewalCodeAlreadyUsed",
  EPA_MEMBER_NOT_FOUND: "caseCreateEpaMemberNotFound",
  EPA_TEST_CLAIM_PAYS: "caseCreateEpaTestClaimPays",
  EPA_TEST_CLAIM_PAYS_DUE_TO_PA: "caseCreateEpaTestClaimPaysDueToPa",
  CASE_CREATE_PENDING_TIMEOUT: "caseCreatePendingTimeout",
};

const DEFAULT_ERROR_MESSAGE = "Failed to create prior authorization case";
const PENDING_TIMEOUT_ERROR_MESSAGE = (
  "Case creation is taking longer than expected. Please search for the case in a few minutes before resubmitting."
);

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 120_000;

export class PendingCaseCreateTimeoutError extends Error {
  constructor() {
    super("Timed out waiting for the case to be created.");
    this.name = "PendingCaseCreateTimeoutError";
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls /pa_case/create_status after a pa_case/create request timed out (CASE_CREATE_PENDING).
 * Resolves with the case_id once PA finishes creating the case, or throws
 * PendingCaseCreateTimeoutError if it isn't found within the poll window.
 */
export async function pollPendingCaseCreate(pendingCaseCreateId: number): Promise<number> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);

    const { status, case_id: caseId } = await getPACaseCreateStatusAPI({
      pending_case_create_id: pendingCaseCreateId,
    });

    if (status === "resolved" && caseId) {
      return caseId;
    }

    if (status === "failed") {
      throw new PendingCaseCreateTimeoutError();
    }
  }

  throw new PendingCaseCreateTimeoutError();
}


export const resolvePACaseCreateError = async (error: unknown): Promise<PACaseCreateErrorResolution> => {
  if (!axios.isAxiosError(error)) {
    return {
      caseId: null,
      error: {
        message: DEFAULT_ERROR_MESSAGE,
      },
    };
  }

  const responseData = error.response?.data as PACaseCreateResponseError | undefined;
  const responseErrorCode = responseData?.error_code;
  const responseMessage = responseData?.message || "";
  const responseError = responseMessage.toLowerCase();
  const duplicateCaseId = responseData?.details?.duplicate_case_id;

  if (responseErrorCode === "CASE_CREATE_PENDING" && responseData?.details?.pending_case_create_id) {
    try {
      return {
        caseId: await pollPendingCaseCreate(Number(responseData.details.pending_case_create_id)),
        error: null,
      };
    } catch (pollError) {
      if (pollError instanceof PendingCaseCreateTimeoutError) {
        return createErrorResolution(PENDING_TIMEOUT_ERROR_MESSAGE, "CASE_CREATE_PENDING_TIMEOUT");
      }

      return createErrorResolution(DEFAULT_ERROR_MESSAGE);
    }
  }

  if (responseErrorCode === "RENEWAL_CODE_ALREADY_USED") {
    return createErrorResolution(responseMessage || "This renewal code has already been used", responseErrorCode, responseData?.details);
  }

  if (responseErrorCode === "RENEWAL_CODE_NOT_FOUND") {
    return createErrorResolution(responseMessage || "Renewal code not found", responseErrorCode, responseData?.details);
  }

  if (responseErrorCode === "EPA_MEMBER_NOT_FOUND") {
    return createErrorResolution(
      responseMessage || "Our records indicate that this is not one of our members. Please review the member's insurance demographics and submit to the appropriate party. Thank you in advance.",
      responseErrorCode,
      responseData?.details,
    );
  }

  if (responseErrorCode === "EPA_TEST_CLAIM_PAYS") {
    return createErrorResolution(responseMessage || "No Prior Authorization is required at this time.", responseErrorCode, responseData?.details);
  }

  if (responseErrorCode === "EPA_TEST_CLAIM_PAYS_DUE_TO_PA") {
    return createErrorResolution(responseMessage || "The medication has been previously approved.", responseErrorCode, responseData?.details);
  }

  if (responseError.includes("member not found") || responseError.includes("invalid member")) {
    return createErrorResolution("Member not found", "MEMBER_NOT_FOUND", responseData?.details);
  }

  if (responseError.includes("ndc") || responseError.includes("drug not found")) {
    return createErrorResolution("Invalid drug NDC", "INVALID_NDC", responseData?.details);
  }

  if (responseError.includes("npi") || responseError.includes("prescriber") || responseError.includes("provider")) {
    return createErrorResolution("Provider NPI is invalid or not found", "INVALID_PROVIDER", responseData?.details);
  }

  if (responseError.includes("not covered") || responseError.includes("formulary")) {
    return createErrorResolution("Drug is not covered under member's plan", "DRUG_NOT_COVERED", responseData?.details);
  }

  if (responseError.includes("questionnaire") || responseError.includes("clinical criteria")) {
    return createErrorResolution("Required clinical criteria questions are missing or invalid", "INVALID_QUESTIONNAIRE", responseData?.details);
  }

  if (responseError.includes("duplicate") || duplicateCaseId) {
    return createErrorResolution(responseMessage || "A duplicate case is already in progress", "DUPLICATE_CASE", responseData?.details);
  }

  if (responseError.includes("pa tool")) {
    return createErrorResolution("This client has not been migrated to PA Tool", "PA_TOOL_NOT_MIGRATED", responseData?.details);
  }

  if (responseError.includes("epa configuration") || responseError.includes("configuration")) {
    return createErrorResolution("An ePA configuration is not present for this tenant", "EPA_CONFIG_MISSING", responseData?.details);
  }

  if (responseError.includes("icd") || responseError.includes("diagnosis")) {
    return createErrorResolution("One or more diagnosis codes are invalid", "INVALID_ICD10", responseData?.details);
  }

  return createErrorResolution(DEFAULT_ERROR_MESSAGE, responseErrorCode, responseData?.details);
};

const createErrorResolution = (
  message: string,
  errorCode?: string,
  details?: Record<string, string>,
): PACaseCreateErrorResolution => ({
  caseId: null,
  error: {
    message,
    errorCode,
    details,
    analyticsEvent: errorCode ? ERROR_CODE_TO_ANALYTICS_EVENT[errorCode] : undefined,
  },
});
